import { z } from 'zod';

import { safeRandomUUID, toNavigableUrl } from '@/shared-utilities';

export function isMobileUtMode(searchParams: URLSearchParams | null): boolean {
  if (!searchParams) return false;
  return (
    searchParams.get('mode') === 'mobile' && !!searchParams.get('uniqueId')
  );
}

export function getMobileUtUniqueId(
  searchParams: URLSearchParams | null
): string | null {
  return searchParams?.get('uniqueId') ?? null;
}

export function getMobileUtScreenSize(
  searchParams: URLSearchParams | null
): { width: number; height: number } | null {
  const w = searchParams?.get('screenWidth');
  const h = searchParams?.get('screenHeight');
  if (!w || !h) return null;
  const width = parseInt(w, 10);
  const height = parseInt(h, 10);
  if (isNaN(width) || isNaN(height) || width <= 0 || height <= 0) return null;
  return clampHyperbeamDimensions({ width, height });
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.round(value)));
}

export function clampHyperbeamDimensions({
  width,
  height,
}: {
  width: number;
  height: number;
}): { width: number; height: number } {
  return {
    width: clamp(width, 320, 3840),
    height: clamp(height, 320, 2160),
  };
}

const SCREENER_SESSION_KEY_PARAM = 'screenerSessionKey';

/**
 * The screener's key, relayed to the page inside the VM. The phone passes the
 * screener itself and only streams the VM, so without the key the VM page would
 * put the respondent through the screener a second time — and the key is
 * single-use, spent by whichever page starts the unified session.
 */
export function getMobileUtScreenerSessionKey(
  searchParams: URLSearchParams | null
): string | null {
  if (!isMobileUtMode(searchParams)) return null;

  return searchParams?.get(SCREENER_SESSION_KEY_PARAM) || null;
}

export function removeMobileUtScreenerSessionKey(href: string): string {
  const url = new URL(href);
  url.searchParams.delete(SCREENER_SESSION_KEY_PARAM);

  return url.toString();
}

const MOBILE_UT_RESERVED_PARAMS: readonly string[] = [
  'uniqueId',
  'mode',
  'sessionId',
  SCREENER_SESSION_KEY_PARAM,
  'screenWidth',
  'screenHeight',
];

/**
 * `sessionId`, `screenWidth` and `screenHeight` reach the VM and are read by
 * nothing there, in V1 as in V2: the page the VM opens mints its own session
 * server-side, and `getMobileUtScreenSize` — the only reader of the two
 * dimensions — runs on the phone that creates the session, never on the page
 * inside the VM, which skips the phone shell entirely. A screener-gated V2
 * project has no `sessionId` at all, since nothing is created before the
 * respondent qualifies.
 */
export function buildHyperbeamStartUrl({
  projectId,
  uniqueId,
  sessionId = null,
  screenerSessionKey = null,
  forwardedParams,
}: {
  projectId: string;
  uniqueId: string;
  sessionId?: string | null;
  screenerSessionKey?: string | null;
  forwardedParams: URLSearchParams | null;
}): string {
  const basePath = process.env.NEXT_PUBLIC_ASSET_BASE_PATH || '';
  const params = new URLSearchParams(forwardedParams ?? undefined);
  for (const reserved of MOBILE_UT_RESERVED_PARAMS) {
    params.delete(reserved);
  }
  params.set('uniqueId', uniqueId);
  params.set('mode', 'mobile');
  if (sessionId !== null) {
    params.set('sessionId', sessionId);
  }
  if (screenerSessionKey !== null) {
    params.set(SCREENER_SESSION_KEY_PARAM, screenerSessionKey);
  }
  if (typeof window !== 'undefined') {
    params.set('screenWidth', String(window.innerWidth));
    params.set('screenHeight', String(window.innerHeight));
  }
  return `${window.location.origin}${basePath}/project/${projectId}?${params.toString()}`;
}

const mobileUtEndReasonSchema = z.object({
  reason: z.string(),
  redirectUrl: z.string(),
});

// `endMobileSession` has no redirect field, so the URL rides in `reason`.
export function encodeMobileUtEndReason({
  reason,
  redirectUrl,
}: {
  reason: string;
  redirectUrl: string | null;
}): string {
  return redirectUrl === null
    ? reason
    : JSON.stringify({ reason, redirectUrl });
}

export function parseMobileUtEndRedirectUrl(
  reason: string | null | undefined
): string | null {
  if (!reason) return null;

  let decoded: unknown;
  try {
    decoded = JSON.parse(reason);
  } catch {
    return null;
  }

  const parsed = mobileUtEndReasonSchema.safeParse(decoded);
  return parsed.success ? toNavigableUrl(parsed.data.redirectUrl) : null;
}

export function generateMobileUtUniqueId(): string {
  return safeRandomUUID();
}

export function isMobileAppDebugMode(
  searchParams: URLSearchParams | null
): boolean {
  if (process.env.NODE_ENV === 'production') return false;
  if (!searchParams) return false;
  return searchParams.get('debug') === 'true';
}

const TRANSCRIPT_UPDATE_THROTTLE_MS = 300;

export function shouldSendTranscriptUpdate(
  isFinal: boolean,
  lastSentAtMs: number | null,
  nowMs: number,
  throttleMs: number = TRANSCRIPT_UPDATE_THROTTLE_MS
): boolean {
  if (isFinal) {
    return true;
  }
  if (lastSentAtMs === null) {
    return true;
  }
  return nowMs - lastSentAtMs >= throttleMs;
}
